import { useEffect, useState } from "react";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtNum } from "@/lib/api";
import { PageHeader, Panel, Th, Td, btn, field } from "@/components/ui-kit";

const EMPTY = { part_no: "", ep_sku: "", buyer_item: "", upc: "", type: "FG", uom: "EA", description: "", units_per_pallet: 0, unit_weight_lb: 0, weekly_forecast: 0, size: "", pallet_net_kg: 0, pallet_gross_kg: 0, pallets_per_ftl: 0, moq_pallets: 0, mondi_grade: "", mondi_grade_code: "", grade_name: "", gsm: 0, reel_width_in: 0, reel_width_mm: 0, reel_diameter_mm: 0, groupo_code: "", customer_items: {} };
const NUMS = ["units_per_pallet", "unit_weight_lb", "weekly_forecast", "pallet_net_kg", "pallet_gross_kg", "pallets_per_ftl", "moq_pallets", "gsm", "reel_width_in", "reel_width_mm", "reel_diameter_mm"];
const RM_FIELDS = [
  ["mondi_grade", "Mondi grade"], ["mondi_grade_code", "Mondi grade code"], ["grade_name", "Mondi grade name"], ["gsm", "Grammage (gsm)"],
  ["reel_width_in", "Reel width (inch)"], ["reel_width_mm", "Reel width (mm)"], ["reel_diameter_mm", "Reel diameter (mm)"], ["groupo_code", "Group O internal number"],
];
const FIELDS = [
  ["part_no", "Vendor part # (VP / PAC code)"], ["ep_sku", "EPG SAP code (VN)"], ["buyer_item", "Buyer item # (IN/BP)"], ["upc", "UPC case code"],
  ["description", "Description"], ["uom", "UOM"], ["units_per_pallet", "Units / pallet"], ["unit_weight_lb", "Unit weight (lb)"], ["weekly_forecast", "Weekly forecast (units)"],
  ["size", "Size"], ["pallet_net_kg", "Net weight / pallet (kg)"], ["pallet_gross_kg", "Gross weight / pallet (kg)"], ["pallets_per_ftl", "Pallets per FTL"], ["moq_pallets", "MOQ (pallets)"],
];

const CustItems = ({ f, setF, partners }) => (
  <div data-testid="item-customer-items">
    <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">Customer item numbers (SAP IDoc E2EDP19 001)</span>
    <div className="grid gap-3 sm:grid-cols-3">
      {partners.map((p) => (
        <label key={p.code} className="block"><span className="mb-1 block text-xs text-slate-400">{p.name}</span>
          <Input value={(f.customer_items || {})[p.code] || ""} onChange={(e) => setF({ ...f, customer_items: { ...(f.customer_items || {}), [p.code]: e.target.value } })} className={field} data-testid={`item-cust-${p.code}`} />
        </label>
      ))}
    </div>
  </div>
);

const ItemDialog = ({ item, onClose, onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [partners, setPartners] = useState([]);
  useEffect(() => { api.get("/partners").then((r) => setPartners(r.data.partners)); }, []);
  useEffect(() => {
    if (item) setF({ ...EMPTY, ...item });
  }, [item]);
  const save = async () => {
    try {
      const body = { ...f, ...Object.fromEntries(NUMS.map((k) => [k, Number(f[k]) || 0])) };
      item.id ? await api.put(`/items/${item.id}`, body) : await api.post("/items", body);
      toast.success("Item mapping saved");
      onDone();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-slate-800 bg-[#1F232C] text-slate-100" data-testid="item-dialog">
        <DialogHeader><DialogTitle className="font-heading">{item?.id ? `Edit ${item.part_no}` : "New item"}</DialogTitle></DialogHeader>
        <div className="flex gap-1.5">
          {["FG", "RM"].map((t) => (
            <button key={t} onClick={() => setF({ ...f, type: t })} data-testid={`item-type-${t}`} className={`rounded-sm border px-3 py-1 font-mono text-xs ${f.type === t ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-slate-700 text-slate-400"}`}>{t === "FG" ? "Finished good" : "Raw material"}</button>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {[...FIELDS, ...(f.type === "RM" ? RM_FIELDS : [])].map(([k, l]) => (
            <label key={k} className="block">
              <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-500">{l}</span>
              <Input value={f[k] ?? ""} disabled={k === "part_no" && !!item?.id} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={field} data-testid={`item-field-${k}`} />
            </label>
          ))}
        </div>
        <CustItems f={f} setF={setF} partners={partners} />
        <Button onClick={save} className={btn.primary} data-testid="item-save-btn">Save item</Button>
      </DialogContent>
    </Dialog>
  );
};

export default function Items() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/items").then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, []);
  const del = (i) => {
    if (!window.confirm(`Delete item ${i.part_no} (SAP ${i.ep_sku || "-"})? Orders already received keep their lines, but new orders for it will stop matching.`)) return;
    api.delete(`/items/${i.id}`).then(() => { toast.success(`Item ${i.part_no} deleted`); load(); }).catch((e) => toast.error(errMsg(e)));
  };
  const s = q.trim().toLowerCase();
  const shown = !s ? rows : rows.filter((i) => [i.part_no, i.ep_sku, i.buyer_item, i.upc, i.description, ...Object.values(i.customer_items || {})].some((v) => String(v || "").toLowerCase().includes(s)));
  return (
    <div>
      <PageHeader title="Item master" subtitle="Maintain materials: EP item (VN), SAP code, buyer / customer item numbers, GTIN, units per pallet and weights used to read orders and build IDocs and EDI files. Every change is in the Change Log.">
        <Button onClick={() => setEdit({})} className={btn.primary} data-testid="items-new-btn"><Plus className="mr-1.5 h-4 w-4" /> New item</Button>
      </PageHeader>
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search part, SAP code, customer item, GTIN or description" className={`pl-9 ${field}`} data-testid="items-search" />
      </div>
      <p className="mb-2 text-xs text-slate-500" data-testid="items-count">{shown.length} of {rows.length} items</p>
      <Panel>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="items-table">
            <thead className="border-b border-slate-800"><tr><Th>Type</Th><Th>VP part</Th><Th>EPG SAP (VN)</Th><Th>Buyer item</Th><Th>Description</Th><Th>UOM</Th><Th>Size</Th><Th>Units/pallet</Th><Th>Gross kg/pallet</Th><Th>Pallets/FTL</Th><Th>MOQ pallets</Th><Th>Weekly fcst</Th><Th></Th></tr></thead>
            <tbody className="divide-y divide-slate-800/70">
              {shown.map((i) => (
                <tr key={i.id} data-testid={`item-row-${i.part_no}`}>
                  <Td className={`font-mono ${i.type === "RM" ? "text-amber-300" : "text-emerald-300"}`}>{i.type}</Td>
                  <Td className="font-mono text-slate-100">{i.part_no}</Td><Td className="font-mono">{i.ep_sku}</Td><Td className="font-mono">{i.buyer_item}</Td>
                  <Td className="max-w-[280px] truncate">{i.description}</Td><Td className="font-mono">{i.uom}</Td>
                  <Td className="whitespace-nowrap font-mono text-xs">{i.size}</Td><Td>{fmtNum(i.units_per_pallet)}</Td><Td>{fmtNum(i.pallet_gross_kg)}</Td>
                  <Td>{fmtNum(i.pallets_per_ftl)}</Td><Td>{fmtNum(i.moq_pallets)}</Td><Td>{fmtNum(i.weekly_forecast)}</Td>
                  <Td className="whitespace-nowrap text-right"><Button size="sm" variant="ghost" onClick={() => setEdit(i)} className={btn.ghost} data-testid={`item-edit-${i.part_no}`}><Pencil className="h-3.5 w-3.5" /><span className="sr-only">Edit</span></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(i)} className={`${btn.ghost} hover:text-rose-400`} data-testid={`item-delete-${i.part_no}`}><Trash2 className="h-3.5 w-3.5" /><span className="sr-only">Delete</span></Button></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <ItemDialog item={edit} onClose={() => setEdit(null)} onDone={load} />
    </div>
  );
}
